import type { ReactNode } from 'react';
import { Card, FailedState, LoadingSkeleton, StatusChip, Toggle } from '@pointfive/ui';
import type { ProcedureState } from '@/client/useProcedure';

/** Shared pieces for the Settings sections.
 *
 *  Every literal here is copied from `design-reference/CLAUDE.md` — the design language the package's own screens
 *  follow. These sections have no reference markup to be faithful to, so the language *is* the specification: card
 *  radius and padding, the 9.5px uppercase section label, 12.5px body, 10.5px secondary, the pill toggle. Nothing is
 *  invented, and no colour is written as a hex.
 */

/** A labelled row with its explanation on the left and its control on the right — the grammar the package's own
 *  Notifications section uses, so a new section reads as part of the same screen. */
export function SettingRow({
  label,
  hint,
  children,
  danger = false,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  danger?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '13px 0',
        borderTop: '1px solid var(--l5)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', fontWeight: 500, color: danger ? 'var(--neg)' : 'var(--text)' }}>
          {label}
        </div>
        {hint === undefined ? null : (
          <div style={{ fontSize: '11px', color: 'var(--muted2)', marginTop: '2px', lineHeight: 1.5 }}>
            {hint}
          </div>
        )}
      </div>
      {children === undefined ? null : <div style={{ flex: 'none' }}>{children}</div>}
    </div>
  );
}

/** The package's own toggle, driven from a boolean. Its three style values are the reference's literals; passing them
 *  through one place stops each section inventing its own nearly-identical switch. */
export function SettingToggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <Toggle
      trackBg={on ? 'var(--acc-fill)' : 'var(--l10)'}
      knob={on ? 'translateX(14px)' : 'translateX(0)'}
      knobBg={on ? 'var(--rail-ink)' : 'var(--muted)'}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={on}
    />
  );
}

/** A status pill with a tone rather than two raw colours.
 *
 *  `@pointfive/ui`'s `StatusChip` takes `bg`/`fg` because it was extracted from markup that had them inline. These
 *  sections have a small vocabulary of states — good, bad, waiting, inert — and naming them here keeps the same four
 *  colour pairs from being retyped in six files. */
export function ToneChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  const [bg, fg] = TONES[tone];
  return (
    <StatusChip bg={bg} fg={fg}>
      {children}
    </StatusChip>
  );
}

export type Tone = 'pos' | 'neg' | 'warn' | 'mute';
const TONES: Record<Tone, [string, string]> = {
  pos: ['color-mix(in srgb, var(--pos) 16%, transparent)', 'var(--pos)'],
  neg: ['color-mix(in srgb, var(--neg) 18%, transparent)', 'var(--neg)'],
  warn: ['color-mix(in srgb, var(--amb) 18%, transparent)', 'var(--amb-2)'],
  mute: ['var(--l6)', 'var(--muted)'],
};

/** A ghost button that can be disabled and busy.
 *
 *  The kit's own `GhostButton` has neither, and these sections need both: every action here talks to the server, and
 *  a button that stays pressable while its request is in flight is one a nervous trader will press twice. Same
 *  literals as the design's ghost button — 26–28px, 9px radius, `var(--l8)` border. */
export function ActionButton({
  onClick,
  disabled = false,
  danger = false,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      style={{
        fontSize: '11px',
        fontWeight: 600,
        padding: '5px 12px',
        borderRadius: '99px',
        border: `1px solid ${danger ? 'color-mix(in srgb, var(--neg) 45%, transparent)' : 'var(--l8)'}`,
        background: 'transparent',
        color: danger ? 'var(--neg)' : 'var(--text-mid)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}

/** A card with a heading and the design's own section label above its rows. */
export function SettingCard({
  title,
  sub,
  extra,
  children,
}: {
  title: string;
  sub?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card padding="roomy">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '9.5px',
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              color: 'var(--muted2)',
              fontWeight: 600,
            }}
          >
            {title}
          </div>
          {sub === undefined ? null : (
            <div style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '5px', lineHeight: 1.55 }}>
              {sub}
            </div>
          )}
        </div>
        {extra === undefined ? null : <div style={{ flex: 'none' }}>{extra}</div>}
      </div>
      <div style={{ marginTop: '6px' }}>{children}</div>
    </Card>
  );
}

/** Renders the four states a real read has.
 *
 *  Loading is a skeleton rather than an empty card, and a failure says so rather than rendering zeroes: a Settings
 *  screen showing "0 active sessions" because a request failed is worse than one that admits it could not tell. */
export function Section<T>({
  state,
  reload,
  children,
}: {
  state: ProcedureState<T>;
  reload: () => void;
  children: (data: T) => ReactNode;
}) {
  if (state.status === 'loading')
    return (
      <Card padding="roomy">
        <LoadingSkeleton rows={4} />
      </Card>
    );
  if (state.status === 'failed')
    return (
      <Card padding="roomy">
        <FailedState what="this section" detail={state.error.message} onRetry={reload} />
      </Card>
    );
  return <>{children(state.data)}</>;
}

/** A short relative time — "3 minutes ago". Absolute timestamps are kept in the `title`, because "2026-08-23T09:14Z"
 *  is the right answer to "exactly when?" and the wrong one to "is this recent?". */
export function ago(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '—';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** A future time — "in 4 hours". Same reasoning as `ago`, in the other direction. */
export function until(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '—';
  const minutes = Math.max(0, Math.round((then - now) / 60000));
  if (minutes < 1) return 'any moment';
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}
